import { useState, useEffect, useCallback } from 'react';
import { isAnonymousUser } from '../utils/userUtils';

const MESSAGE_LIMIT = 5;
const STORAGE_KEY = 'anonymous_message_count';

/**
 * Hook para gestionar el límite de mensajes de usuarios anónimos
 */
export const useAnonymousMessageLimit = () => {
    // Feature flag desde .env
    const isFeatureEnabled = process.env.REACT_APP_ENABLE_MESSAGE_LIMIT === 'true';
    
    const [messageCount, setMessageCount] = useState(() => {
        if (!isFeatureEnabled) return 0;
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? parseInt(stored, 10) : 0;
    });

    const [isLimitReached, setIsLimitReached] = useState(false);

    // Verificar si el usuario puede enviar mensajes
    const canSendMessage = useCallback(() => {
        // Si el feature está deshabilitado, permitir siempre
        if (!isFeatureEnabled) return true;
        
        // Si es usuario autenticado, permitir siempre
        if (!isAnonymousUser()) return true;
        
        // Si es anónimo, verificar límite
        return messageCount < MESSAGE_LIMIT;
    }, [isFeatureEnabled, messageCount]);

    // Incrementar contador
    const incrementCount = useCallback(() => {
        if (!isFeatureEnabled || !isAnonymousUser()) return;

        setMessageCount(prev => {
            const newCount = prev + 1;
            localStorage.setItem(STORAGE_KEY, newCount.toString());
            
            if (newCount >= MESSAGE_LIMIT) {
                setIsLimitReached(true);
            }
            
            return newCount;
        });
    }, [isFeatureEnabled]);

    // Resetear contador (al iniciar sesión)
    const resetCount = useCallback(() => {
        setMessageCount(0);
        setIsLimitReached(false);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    // Auto-reset al detectar cambio de usuario anónimo a autenticado
    useEffect(() => {
        if (!isAnonymousUser()) {
            resetCount();
        }
    }, [resetCount]);

    // Verificar estado inicial
    useEffect(() => {
        if (isFeatureEnabled && isAnonymousUser() && messageCount >= MESSAGE_LIMIT) {
            setIsLimitReached(true);
        }
    }, [isFeatureEnabled, messageCount]);

    return {
        messageCount,
        messageLimit: MESSAGE_LIMIT,
        isLimitReached,
        canSendMessage,
        incrementCount,
        resetCount,
        isFeatureEnabled,
        remainingMessages: Math.max(0, MESSAGE_LIMIT - messageCount)
    };
};